import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, ShieldAlert, Coins, LineChart, ArrowUpRight, ArrowDownRight, Wallet } from "lucide-react";
import { wealthApi } from "@/api/wealth";

// WEALTH & MONEY SURFACE — real-time crypto + equity portfolio (READ-ONLY).
// Founder-gated on the server (fail-closed 403). Galaxy S20 chassis: high-density,
// Neo-Brutalist only — border-2 border-black, flat solid black offset shadows,
// no gradients/blurs, high-contrast mono metric blocks.

const money = (v, dash = "—") => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return dash;
  const n = Number(v);
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const num = (v, max = 4) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: max });
};
const pct = (v) => {
  const n = Number(v) || 0;
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
};

function Delta({ value, testid }) {
  const n = Number(value) || 0;
  const up = n >= 0;
  return (
    <span
      data-testid={testid}
      className={[
        "inline-flex items-center gap-0.5 border-2 border-black px-1.5 py-0.5 font-mono text-[10px] font-black",
        up ? "bg-[#98FFB6] text-black" : "bg-[#FF6B6B] text-black",
      ].join(" ")}
    >
      {up ? <ArrowUpRight size={11} strokeWidth={3} /> : <ArrowDownRight size={11} strokeWidth={3} />}
      {pct(n)}
    </span>
  );
}

function Frame({ children, testid, className = "" }) {
  return (
    <div
      data-testid={testid}
      className={`border-2 border-black shadow-[4px_4px_0px_#000] ${className}`}
    >
      {children}
    </div>
  );
}

export default function WealthMonitor() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | forbidden | error
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await wealthApi.portfolio();
      setData(res);
      setStatus("ok");
    } catch (e) {
      const code = e?.response?.status;
      setStatus(code === 403 ? "forbidden" : "error");
    } finally {
      if (isRefresh) setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (status === "loading") {
    return (
      <div className="grid place-items-center py-16" data-testid="wealth-monitor-loading">
        <Loader2 size={26} className="animate-spin text-black" />
        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-black/60">Loading live portfolio…</p>
      </div>
    );
  }

  if (status === "forbidden") {
    return (
      <Frame testid="wealth-monitor-restricted" className="p-5 bg-[#FF6B6B]">
        <div className="flex items-center gap-2">
          <ShieldAlert size={18} strokeWidth={2.6} className="text-black" />
          <p className="font-mono text-[12px] font-black uppercase tracking-widest text-black">403 · Restricted</p>
        </div>
        <p className="mt-2 font-mono text-[10px] leading-relaxed text-black/80">
          Real-time financial asset tracking is restricted to authorized operators only.
        </p>
      </Frame>
    );
  }

  if (status === "error") {
    return (
      <Frame testid="wealth-monitor-error" className="p-5 bg-white">
        <p className="font-mono text-[11px] font-black uppercase tracking-widest text-black">Data unavailable</p>
        <p className="mt-1.5 font-mono text-[10px] text-black/60">Live pipelines did not respond. Try refreshing.</p>
        <button
          data-testid="wealth-monitor-retry"
          onClick={() => load(true)}
          className="mt-3 flex items-center gap-1.5 border-2 border-black bg-black px-3 py-2 font-mono text-[10px] font-black uppercase tracking-widest text-white shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
        >
          <RefreshCw size={12} /> Retry
        </button>
      </Frame>
    );
  }

  const crypto = data?.crypto || { holdings: [], total_usd: 0 };
  const shares = data?.shares || { holdings: [], total_value: 0, total_gain: 0, total_gain_pct: 0 };
  const netWorth = data?.net_worth ?? 0;

  return (
    <div className="space-y-3.5" data-testid="wealth-monitor">
      {/* Macro Metric Badge — Total Combined Net Worth */}
      <Frame testid="wealth-networth-badge" className="bg-black p-4">
        <div className="flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FFCC00]">Your Total Money</p>
          <button
            data-testid="wealth-refresh-btn"
            onClick={() => load(true)}
            className="grid h-7 w-7 place-items-center border-2 border-[#FFCC00] bg-black text-[#FFCC00] active:translate-y-[1px]"
            aria-label="Refresh"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
        <p data-testid="wealth-networth-value" className="mt-1.5 font-mono text-5xl font-black leading-none tracking-tight text-white">
          {money(netWorth, "$0.00")}
        </p>
        <div className="mt-2.5 grid grid-cols-2 gap-2">
          <div className="border-2 border-[#FFCC00] bg-white/[0.06] px-2.5 py-1.5">
            <p className="font-mono text-[8px] uppercase tracking-widest text-[#98FFB6]">Crypto</p>
            <p data-testid="wealth-crypto-total" className="font-mono text-sm font-black text-white">{money(crypto.total_usd, "$0.00")}</p>
          </div>
          <div className="border-2 border-[#FFCC00] bg-white/[0.06] px-2.5 py-1.5">
            <p className="font-mono text-[8px] uppercase tracking-widest text-[#A4DCFA]">Shares</p>
            <p data-testid="wealth-shares-total" className="font-mono text-sm font-black text-white">{money(shares.total_value, "$0.00")}</p>
          </div>
        </div>
      </Frame>

      {/* Crypto Holdings Panel */}
      <div data-testid="wealth-crypto-panel">
        <div className="mb-2 flex items-center gap-2 border-2 border-black bg-[#98FFB6] px-3 py-2 shadow-[3px_3px_0px_#000]">
          <Coins size={15} strokeWidth={2.6} className="text-black" />
          <p className="font-mono text-[11px] font-black uppercase tracking-widest text-black">Crypto Holdings</p>
          <span className="ml-auto font-mono text-[10px] font-black text-black/70">{crypto.holdings?.length || 0}</span>
        </div>
        {crypto.holdings?.length ? (
          <div className="space-y-2">
            {crypto.holdings.map((h, i) => (
              <Frame key={`${h.symbol}-${i}`} testid={`wealth-crypto-row-${i}`} className="p-3 bg-white">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-mono text-[13px] font-black uppercase tracking-wide text-black truncate">{h.symbol || "—"}</p>
                    <p className="font-mono text-[9px] text-black/55 truncate">{num(h.amount, 6)} @ {money(h.price_usd)}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-mono text-sm font-black text-black">{money(h.value_usd)}</p>
                    <Delta value={h.change_24h} testid={`wealth-crypto-delta-${i}`} />
                  </div>
                </div>
              </Frame>
            ))}
          </div>
        ) : (
          <Frame testid="wealth-crypto-empty" className="p-4 bg-[#F5F5F5]">
            <div className="flex items-center gap-2">
              <Wallet size={14} className="text-black/50" />
              <p className="font-mono text-[10px] uppercase tracking-widest text-black/55">No wallets tracked · $0.00</p>
            </div>
          </Frame>
        )}
      </div>

      {/* Shares Holdings Panel */}
      <div data-testid="wealth-shares-panel">
        <div className="mb-2 flex items-center gap-2 border-2 border-black bg-[#A4DCFA] px-3 py-2 shadow-[3px_3px_0px_#000]">
          <LineChart size={15} strokeWidth={2.6} className="text-black" />
          <p className="font-mono text-[11px] font-black uppercase tracking-widest text-black">Shares Holdings</p>
          <span className="ml-auto font-mono text-[10px] font-black text-black/70">{shares.holdings?.length || 0}</span>
        </div>
        {shares.holdings?.length ? (
          <div className="space-y-2">
            {shares.holdings.map((s, i) => (
              <Frame key={`${s.ticker}-${i}`} testid={`wealth-share-row-${i}`} className="p-3 bg-white">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-mono text-[13px] font-black uppercase tracking-wide text-black truncate">{s.ticker}</p>
                    <p className="font-mono text-[9px] text-black/55 truncate">{num(s.shares, 4)} sh · avg {money(s.cost_basis)}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-mono text-sm font-black text-black">{money(s.market_value)}</p>
                    <p className="font-mono text-[9px] text-black/55">px {money(s.price)}</p>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between border-t-2 border-black/10 pt-2">
                  <Delta value={s.change_pct} testid={`wealth-share-delta-${i}`} />
                  <div className="text-right">
                    <span
                      data-testid={`wealth-share-gain-${i}`}
                      className={`font-mono text-[11px] font-black ${Number(s.gain) >= 0 ? "text-emerald-700" : "text-red-700"}`}
                    >
                      {s.gain === null || s.gain === undefined ? "—" : `${Number(s.gain) >= 0 ? "+" : ""}${money(s.gain)}`}
                      {" "}({pct(s.gain_pct)})
                    </span>
                  </div>
                </div>
              </Frame>
            ))}
          </div>
        ) : (
          <Frame testid="wealth-shares-empty" className="p-4 bg-[#F5F5F5]">
            <div className="flex items-center gap-2">
              <LineChart size={14} className="text-black/50" />
              <p className="font-mono text-[10px] uppercase tracking-widest text-black/55">No shares tracked · $0.00</p>
            </div>
          </Frame>
        )}
      </div>
    </div>
  );
}
